import { z } from 'zod';
// Reuse the existing BankAccountType enum (schema.prisma enum
// BankAccountType { CURRENT SAVINGS FIXED FOREIGN }). It is already exported
// app-wide from bank-account.ts — re-importing it (instead of redeclaring a
// duplicate `BankAccountTypeSchema`) avoids a TS2308 barrel-ambiguity collision.
import { BankAccountTypeSchema } from './bank-account.js';
export const ConfirmationMethodSchema = z.enum(['PHONE', 'LETTER', 'EMAIL_TO_VENDOR']);
export const VendorBankChangeStatusSchema = z.enum([
    'DRAFT',
    'PENDING',
    'APPLIED',
    'REJECTED',
    'RETURNED',
]);
// POST …/bank-change — create a DRAFT request. The route supplies companyId +
// supplierId + vendorId; the body carries the proposed new* bank + a reason.
// All new* fields optional individually (a partial change inherits the rest from
// the Vendor snapshot server-side) but the account number, when present, is
// non-empty.
export const CreateVendorBankChangeSchema = z.object({
    newBankAccountNumber: z.string().min(1).optional(),
    newBankCode: z.string().min(1).optional(),
    newBankBranchCode: z.string().min(1).optional(),
    newAccountHolderName: z.string().min(1).optional(),
    newBankAccountType: BankAccountTypeSchema.optional(),
    reason: z.string().max(500).optional(),
});
// POST …/bank-change/:reqId/confirm — record the out-of-band attestation (D3).
export const ConfirmVendorBankChangeSchema = z.object({
    confirmationMethod: ConfirmationMethodSchema,
    confirmedContact: z.string().min(1).max(200),
});
// approve/reject/return reuse the generic engine bodies (step + comment/reason),
// re-exported under VBC names so the controller imports stay self-documenting.
export const ApproveVendorBankChangeSchema = z.object({
    step: z.coerce.number().int().positive(),
    comment: z.string().optional(),
    // M7-P3: audited SoD-override justification, threaded to the engine.
    sodOverrideReason: z.string().trim().min(1).optional(),
});
export const RejectVendorBankChangeSchema = z.object({
    step: z.coerce.number().int().positive(),
    reason: z.string().min(1),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
export const ReturnVendorBankChangeSchema = z.object({
    step: z.coerce.number().int().positive(),
    reason: z.string().min(1),
    sodOverrideReason: z.string().trim().min(1).optional(),
});
// Read shape — one VBC request row (drives the panel old→new diff + timeline).
export const VendorBankChangeApprovalRowSchema = z.object({
    stepNo: z.number().int(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable(),
    approvedBy: z.string().nullable(),
    comment: z.string().nullable(),
});
export const VendorBankChangeSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string(),
    vendorId: z.uuid(),
    requestDate: z.coerce.date(),
    reason: z.string().nullable().optional(),
    oldBankAccountNumber: z.string().nullable().optional(),
    oldBankCode: z.string().nullable().optional(),
    oldBankBranchCode: z.string().nullable().optional(),
    oldAccountHolderName: z.string().nullable().optional(),
    oldBankAccountType: BankAccountTypeSchema.nullable().optional(),
    newBankAccountNumber: z.string().nullable().optional(),
    newBankCode: z.string().nullable().optional(),
    newBankBranchCode: z.string().nullable().optional(),
    newAccountHolderName: z.string().nullable().optional(),
    newBankAccountType: BankAccountTypeSchema.nullable().optional(),
    confirmationMethod: ConfirmationMethodSchema.nullable().optional(),
    confirmedContact: z.string().nullable().optional(),
    confirmedAt: z.coerce.date().nullable().optional(),
    confirmedBy: z.string().nullable().optional(),
    status: VendorBankChangeStatusSchema,
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
    approvals: z.array(VendorBankChangeApprovalRowSchema).optional(),
});
//# sourceMappingURL=vendor-bank-change.js.map